import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Clock, Star, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { useProgress } from '../contexts/ProgressContext.js';
import LoadingSpinner from '../components/common/LoadingSpinner.js';
import SafetyNotice from '../components/common/SafetyNotice.js';

const WOWContent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { completeWorkout, workoutHistory } = useProgress();
  
  const [wowContent, setWowContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [completedWeeks, setCompletedWeeks] = useState([]);

  // Get the API base URL
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';

  useEffect(() => {
    console.log('WOWContent component mounted');
    
    // Check for month parameter in URL first
    const urlParams = new URLSearchParams(window.location.search);
    const month = urlParams.get('month');
    
    if (month) {
      console.log('Month parameter found:', month);
      fetchWOWContentForMonth(month);
    } else {
      console.log('No month parameter, fetching current month content');
      fetchWOWContent();
    }
  }, []);

  // Load completed weeks from workout history
  useEffect(() => {
    if (user && workoutHistory && wowContent?.subscriptionMonth) {
      const month = wowContent.subscriptionMonth;
      const wowWeeks = workoutHistory
        .filter(workout => {
          if (!workout.id) return false;
          
          // For month 1, match both old format (wow-X) and new format (month1-wow-X)
          // For months 2+, only match new format (monthX-wow-X)
          if (month === 1) {
            return workout.id.includes(`month1-wow-`) || 
                   (workout.id.includes('wow-') && !workout.id.includes('month'));
          } else {
            return workout.id.includes(`month${month}-wow-`);
          }
        })
        .map(workout => {
          // Extract week number from workout ID
          let weekMatch;
          if (workout.id.includes(`month${month}-wow-`)) {
            weekMatch = workout.id.match(new RegExp(`month${month}-wow-(\\d+)`));
          } else if (month === 1 && workout.id.includes('wow-') && !workout.id.includes('month')) {
            // Legacy format - only for month 1
            weekMatch = workout.id.match(/wow-(\d+)/);
          }
          return weekMatch ? parseInt(weekMatch[1]) : null;
        })
        .filter(week => week !== null);
      
      setCompletedWeeks(wowWeeks);
      console.log('Found completed WOW weeks for month', month, ':', wowWeeks);
    }
  }, [user, workoutHistory, wowContent?.subscriptionMonth]);

  // Don't show content while subscription is loading
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading WOW content...</p>
        </div>
      </div>
    );
  }

  const userTier = subscription?.tier || 'basic';

  const fetchWOWContent = async () => {
    try {
      setLoading(true);
      console.log('Fetching WOW content from:', `${apiBaseUrl}/monthly-packages/current/month`);
      const response = await fetch(`${apiBaseUrl}/monthly-packages/current/month?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch WOW content');
      }

      const data = await response.json();
      setWowContent(data);
    } catch (err) {
      console.error('Error fetching WOW content:', err);
      setError('Failed to load WOW content');
    } finally {
      setLoading(false);
    }
  };

  const fetchWOWContentForMonth = async (month) => {
    try {
      setLoading(true);
      console.log('Fetching WOW content for month:', month);
      const response = await fetch(`${apiBaseUrl}/monthly-packages/month/${month}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch WOW content for month');
      }

      const data = await response.json();
      console.log('WOW content for month', month, ':', data);
      console.log('WOW content workoutOfTheWeek:', data.workoutOfTheWeek);
      console.log('WOW content dailyChallenge:', data.dailyChallenge);
      console.log('WOW content title:', data.title);
      console.log('WOW content theme:', data.theme);
      setWowContent(data);
    } catch (err) {
      console.error('Error fetching WOW content for month:', err);
      setError('Failed to load WOW content for month');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayVideo = async (weekData) => {
    console.log('Playing WOW video for week:', weekData.week);
    
    try {
      // Use the same working format as DailyChallenge.js
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://elderfit.onrender.com/api';
      const response = await fetch(`${apiBaseUrl}/cloudflare-stream/get-video-url?videoId=${weekData.cloudflareStreamId}&tier=${userTier}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to generate secure URL: ${response.statusText}`);
      }
      
      const data = await response.json();
      const videoUrl = data.url;

      if (videoUrl) {
        window.open(videoUrl, '_blank');
      } else {
        alert(`Video: ${weekData.title}\nDuration: ${Math.floor(weekData.duration / 60)} minutes\nFocus: ${weekData.focusAreas.join(', ')}\n\nVideo URL generation failed. Please try again.`);
      }
    } catch (error) {
      console.error('Error loading video:', error);
      alert('Failed to load video. Please try again.');
    }
  };

  const handleCompleteWeek = async (week) => {
    console.log('handleCompleteWeek called for week:', week);
    console.log('Current completedWeeks:', completedWeeks);
    
    if (!completedWeeks.includes(week)) {
      try {
        const selectedWorkout = wowContent.workoutOfTheWeek.find(w => w.week === week);
        if (selectedWorkout && user && completeWorkout) {
          // Convert duration from seconds to minutes, ensure minimum of 1 minute
          const durationMinutes = Math.max(1, Math.round(selectedWorkout.duration / 60));
          
          // Get month number from wowContent
          const month = wowContent.subscriptionMonth || 1;
          
          // Create workout completion data with month identifier
          const workoutId = `month${month}-wow-${week}`;
          const exercisesCompleted = [{
            exerciseId: `month${month}-wow-${week}`,
            name: selectedWorkout.title || `WOW Week ${week}`,
            reps: 1,
            duration: durationMinutes
          }];
          
          console.log('WOW completion data:', {
            workoutId,
            exercisesCompleted,
            durationMinutes,
            originalDuration: selectedWorkout.duration
          });
          
          // Save to progress system using completeWorkout
          const success = await completeWorkout(workoutId, exercisesCompleted, durationMinutes);
          
          if (success) {
            setCompletedWeeks(prev => {
              const newCompleted = [...prev, week];
              console.log('Updated completedWeeks to:', newCompleted);
              return newCompleted;
            });
            console.log(`Week ${week} completed and saved to progress!`);
          } else {
            console.error('Failed to save WOW progress');
            // Still mark as completed locally
            setCompletedWeeks(prev => {
              const newCompleted = [...prev, week];
              console.log('Updated completedWeeks to (fallback):', newCompleted);
              return newCompleted;
            });
          }
        }
      } catch (error) {
        console.error('Error completing WOW week:', error);
        // Still mark as completed locally
        setCompletedWeeks(prev => {
          const newCompleted = [...prev, week];
          console.log('Updated completedWeeks to (error fallback):', newCompleted);
          return newCompleted;
        });
      }
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading WOW Content</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/content/premium')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Premium Content
          </button>
        </div>
      </div>
    );
  }

  if (!wowContent || !wowContent.workoutOfTheWeek) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No WOW Content Available</h2>
          <p className="text-gray-600 mb-4">WOW content is not available for your current subscription.</p>
          <button
            onClick={() => navigate('/content/premium')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Premium Content
          </button>
        </div>
      </div>
    );
  }

  const currentWeek = wowContent.workoutOfTheWeek.find(w => w.week === selectedWeek);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/content/premium')}
                className="mr-4 p-2 rounded-md text-orange-100 hover:text-white hover:bg-orange-700"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-white">🏋️ Workout of the Week</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* WOW Overview */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-orange-200">
          <h2 className="text-2xl font-bold text-orange-800 mb-4">
            {wowContent.title || 'Weekly Workout Programs'}
          </h2>
          <p className="text-orange-700 mb-6">
            {wowContent.description || 'Access weekly workout programs designed to progressively build your strength and fitness.'}
          </p>
          <SafetyNotice className="mb-6" />
          
          {/* Week Navigation */}
          <div className="flex flex-wrap gap-2 mb-6">
            {wowContent.workoutOfTheWeek.map((week) => (
              <button
                key={week.week}
                onClick={() => setSelectedWeek(week.week)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedWeek === week.week
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                Week {week.week}
              </button>
            ))}
          </div>

          {/* Current Week Content */}
          {currentWeek && (
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-300 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-orange-800 mb-2">{currentWeek.title}</h3>
                  <p className="text-orange-700 mb-4">{currentWeek.description}</p>
                </div>
                <div className="flex items-center text-orange-600 bg-orange-100 px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">{Math.floor(currentWeek.duration / 60)} min</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <h4 className="font-semibold text-orange-800 mb-2">Focus Areas</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentWeek.focusAreas.map((area, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-orange-200 text-orange-800 text-sm rounded-full font-medium"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-800 mb-2">Equipment</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentWeek.equipment.map((item, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-amber-200 text-amber-800 text-sm rounded-full font-medium"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handlePlayVideo(currentWeek)}
                  className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-4 rounded-xl hover:from-orange-700 hover:to-amber-700 transition-all font-medium flex items-center justify-center shadow-lg hover:shadow-xl"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start Week {currentWeek.week} Workout
                </button>
                
                {!completedWeeks.includes(currentWeek.week) ? (
                  <button
                    onClick={() => handleCompleteWeek(currentWeek.week)}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-medium flex items-center justify-center shadow-lg hover:shadow-xl"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark Week {currentWeek.week} as Complete
                  </button>
                ) : (
                  <div className="w-full bg-green-100 text-green-800 px-6 py-4 rounded-xl font-medium flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Week {currentWeek.week} Completed
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* All Weeks Overview - Simplified Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {wowContent.workoutOfTheWeek.map((week) => (
            <div
              key={week.week}
              className={`bg-white rounded-xl shadow-md p-4 cursor-pointer transition-all hover:shadow-lg ${
                selectedWeek === week.week
                  ? 'ring-2 ring-orange-500 border-2 border-orange-300'
                  : 'border border-orange-200'
              }`}
              onClick={() => setSelectedWeek(week.week)}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-orange-800">Week {week.week}</h4>
                <div className="flex items-center text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                  <Clock className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium">{Math.floor(week.duration / 60)}m</span>
                </div>
              </div>
              
              <h5 className="font-medium text-orange-800 mb-2">{week.title}</h5>
              <p className="text-orange-600 text-sm mb-3 line-clamp-2">{week.description}</p>
              
              <div className="flex flex-wrap gap-1 mb-3">
                {week.focusAreas.slice(0, 2).map((area, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full"
                  >
                    {area}
                  </span>
                ))}
                {week.focusAreas.length > 2 && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                    +{week.focusAreas.length - 2}
                  </span>
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayVideo(week);
                }}
                className="w-full bg-orange-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors font-medium"
              >
                Start Workout
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WOWContent;
